import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface MarkdownPreviewProps {
  /** Markdown source to render. Plain text renders as-is. */
  source: string;
  /** Optional class for the wrapper div. */
  className?: string;
}

/**
 * Renders Markdown safely (no raw HTML by default).
 * Supports full GFM: tables, bold, italic, strikethrough, headings, lists, links, code, blockquote, task lists.
 */
const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ source, className }) => {
  return (
    <div className={`markdown-preview max-w-none ${className ?? ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children, ...props }) => (
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-4 mb-2 border-b border-gray-200 dark:border-gray-600 pb-1" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-3 mb-1.5" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-2.5 mb-1" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-2 mb-1" {...props}>
              {children}
            </h4>
          ),
          h5: ({ children, ...props }) => (
            <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2 mb-1" {...props}>
              {children}
            </h5>
          ),
          h6: ({ children, ...props }) => (
            <h6 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mt-1.5 mb-0.5" {...props}>
              {children}
            </h6>
          ),
          // Tables
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border border-gray-300 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-gray-100 dark:bg-gray-700/50" {...props}>
              {children}
            </thead>
          ),
          tbody: ({ children, ...props }) => (
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600 bg-white dark:bg-gray-800/50" {...props}>
              {children}
            </tbody>
          ),
          tr: ({ children, ...props }) => (
            <tr className="border-b border-gray-200 dark:border-gray-600" {...props}>
              {children}
            </tr>
          ),
          th: ({ children, ...props }) => (
            <th className="px-3 py-2 text-left text-sm font-bold text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0" {...props}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600 last:border-r-0" {...props}>
              {children}
            </td>
          ),
          // Inline
          strong: ({ children, ...props }) => (
            <strong className="font-bold text-gray-900 dark:text-gray-100" {...props}>
              {children}
            </strong>
          ),
          em: ({ children, ...props }) => (
            <em className="italic text-gray-800 dark:text-gray-200" {...props}>
              {children}
            </em>
          ),
          del: ({ children, ...props }) => (
            <del className="line-through text-gray-500 dark:text-gray-400" {...props}>
              {children}
            </del>
          ),
          a: ({ href, children, ...props }) => (
            <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          ),
          // Code
          code: ({ className: codeClassName, children, ...props }) => {
            const isBlock = codeClassName?.includes('language-');
            if (isBlock) return <code className={codeClassName} {...props}>{children}</code>;
            return (
              <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 text-[0.9em]" {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children, ...props }) => (
            <pre className="my-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-x-auto text-sm text-gray-800 dark:text-gray-200" {...props}>
              {children}
            </pre>
          ),
          // Block
          p: ({ children, ...props }) => (
            <p className="my-1.5 text-gray-800 dark:text-gray-200" {...props}>
              {children}
            </p>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="my-2 pl-3 border-l-4 border-primary/50 text-gray-700 dark:text-gray-300 italic" {...props}>
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-gray-200 dark:border-gray-600" />,
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-inside my-1.5 text-gray-800 dark:text-gray-200 space-y-0.5" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-inside my-1.5 text-gray-800 dark:text-gray-200 space-y-0.5" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-gray-800 dark:text-gray-200" {...props}>
              {children}
            </li>
          ),
          // GFM task list
          input: ({ checked, ...props }) => (
            <input type="checkbox" checked={!!checked} readOnly className="mr-2 align-middle" {...props} />
          ),
        }}
      >
        {source || ''}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownPreview;
